import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import VxConsultLogo from './VxConsultLogo';
import VxCargaCampos, { descreverCarga, pendenciasCarga } from './VxCargaCampos';
import { buscarEnderecoPorCep } from '../../services/programacaoHavanService';
import { formatarDataBr } from '../utils/dataBr';

// Mesmas regras de obrigatoriedade do backend (ProgramacaoHavanService.calcular_pendencias_vx).
const MOTORISTA_OBRIGATORIOS = ['cep', 'logradouro', 'numero', 'bairro', 'cidade', 'estado', 'telefone_residencial', 'telefone_residencial_contato'];
const VEICULO_OBRIGATORIOS = ['telefone_residencial_proprietario', 'telefone_residencial_contato_proprietario'];
const CAMPOS_PROPRIETARIO = ['telefone_residencial_proprietario', 'telefone_residencial_contato_proprietario', 'telefone_comercial_proprietario', 'telefone_comercial_contato_proprietario'];
const TAMANHO_MAXIMO_ARQUIVO = 10 * 1024 * 1024;

const ETAPAS = [
  { id: 'carga', titulo: 'Carga', descricao: 'Informe o tipo de mercadoria e o valor da carga.' },
  { id: 'comprovante', titulo: 'Comprovante', descricao: 'Anexe o comprovante de endereço do motorista.' },
  { id: 'motorista', titulo: 'Motorista', descricao: 'Confira o endereço e um contato do motorista.' },
  { id: 'veiculos', titulo: 'Veículos', descricao: 'Informe se o conjunto é frota própria e o contato do proprietário.' },
  { id: 'revisar', titulo: 'Revisar', descricao: 'Confira os dados antes de enviar para a VX.' }
];
const REVISAR = ETAPAS.length - 1;

const vazio = valor => !String(valor ?? '').trim();
const somenteDigitos = valor => String(valor ?? '').replace(/\D/g, '');

// Máscara progressiva: (47) 3255-0000 para fixo e (47) 99166-1225 para celular.
export const mascararTelefone = valor => {
  const numeros = somenteDigitos(valor).slice(0, 11);
  if (numeros.length <= 2) return numeros.length ? `(${numeros}` : '';
  const ddd = `(${numeros.slice(0, 2)}) `;
  const resto = numeros.slice(2);
  if (resto.length <= 4) return `${ddd}${resto}`;
  const divisao = numeros.length === 11 ? 5 : 4;
  return `${ddd}${resto.slice(0, divisao)}-${resto.slice(divisao)}`;
};

const CAMPOS_TELEFONE_MOTORISTA = ['telefone_residencial_contato', 'telefone_comercial_contato', 'telefone_referencia_contato'];
const CAMPOS_TELEFONE_VEICULO = ['telefone_residencial_contato_proprietario', 'telefone_comercial_contato_proprietario'];

// Telefones vindos do TMS ou do último envio chegam sem formatação.
const mascararTelefones = (origem, campos) => ({
  ...origem,
  ...Object.fromEntries(campos.filter(campo => origem?.[campo]).map(campo => [campo, mascararTelefone(origem[campo])]))
});

const formatarCep = valor => {
  const numeros = somenteDigitos(valor);
  return numeros.length === 8 ? numeros.replace(/^(\d{5})(\d{3})$/, '$1-$2') : valor;
};

const formatarData = formatarDataBr;

const pendenciasMotorista = motorista => MOTORISTA_OBRIGATORIOS.filter(campo => vazio(motorista?.[campo]));
const pendenciasVeiculo = veiculo => VEICULO_OBRIGATORIOS.filter(campo => vazio(veiculo?.[campo]));

const resumoEndereco = m => [
  [m.logradouro, m.numero, m.complemento].filter(Boolean).join(', '),
  m.bairro,
  [m.cidade, m.estado].filter(Boolean).join('/'),
  m.cep ? `CEP ${formatarCep(m.cep)}` : ''
].filter(Boolean).join(' - ');

const nomeVeiculo = indice => (indice === 0 ? 'Cavalo' : `Carreta ${indice}`);

const ProgramacaoHavanVxPesquisaModal = ({ show, row, data, opcoesCarga, carregandoOpcoes, erroOpcoes, loading, erro, onClose, onSubmit }) => {
  const [etapa, setEtapa] = useState(0);
  const [carga, setCarga] = useState({});
  const [motorista, setMotorista] = useState({});
  const [veiculos, setVeiculos] = useState([]);
  const [arquivos, setArquivos] = useState({});
  const [trocar, setTrocar] = useState({});
  const [contatosExtras, setContatosExtras] = useState(false);
  const [comercialVeiculo, setComercialVeiculo] = useState({});
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroArquivo, setErroArquivo] = useState('');

  const documentos = data?.documentos || [];
  const documentosPendentes = documentos.filter(documento => (!documento.anexado || trocar[documento.codigo]) && !arquivos[documento.codigo]);
  const pendentesMotorista = pendenciasMotorista(motorista);
  const pendentesVeiculos = veiculos.map(pendenciasVeiculo);
  const pendenciasPorEtapa = [
    pendenciasCarga(carga, opcoesCarga?.faixas_valor),
    documentosPendentes.length,
    pendentesMotorista.length,
    pendentesVeiculos.reduce((total, lista) => total + lista.length, 0),
    0
  ];
  const totalPendencias = pendenciasPorEtapa.reduce((total, quantidade) => total + quantidade, 0);

  useEffect(() => {
    if (!show || !data) return;
    const veiculosIniciais = [data.cavalo, ...(data.carretas || [])].filter(Boolean);
    // Sugestão da última pesquisa criada para o CPF.
    const cargaInicial = { tipo_mercadoria: data.tipo_mercadoria ?? null, valor: data.valor ?? null, valor_carga: data.valor_carga ?? null };
    setCarga(cargaInicial);
    setMotorista(mascararTelefones(data.motorista, CAMPOS_TELEFONE_MOTORISTA));
    setVeiculos(veiculosIniciais.map(veiculo => mascararTelefones(veiculo, CAMPOS_TELEFONE_VEICULO)));
    setArquivos({});
    setTrocar({});
    setErroArquivo('');
    setContatosExtras(Boolean(data.motorista?.telefone_comercial_contato || data.motorista?.telefone_referencia_contato));
    setComercialVeiculo(Object.fromEntries(veiculosIniciais.map((veiculo, indice) => [indice, Boolean(veiculo.telefone_comercial_contato_proprietario)])));
    // Começa na primeira etapa que precisa de atenção; se tudo veio pronto, vai direto para a revisão.
    const pendencias = [
      pendenciasCarga(cargaInicial, []),
      (data.documentos || []).filter(documento => !documento.anexado).length,
      pendenciasMotorista(data.motorista).length,
      veiculosIniciais.reduce((total, veiculo) => total + pendenciasVeiculo(veiculo).length, 0)
    ];
    const primeiraPendente = pendencias.findIndex(quantidade => quantidade > 0);
    setEtapa(primeiraPendente >= 0 ? primeiraPendente : REVISAR);
  }, [data, show]);

  const alterarMotorista = (campo, mascara) => event => {
    const { value } = event.target;
    setMotorista(atual => ({ ...atual, [campo]: mascara ? mascara(value) : value }));
  };

  const completarPorCep = async () => {
    const cep = somenteDigitos(motorista.cep);
    setMotorista(atual => ({ ...atual, cep: formatarCep(atual.cep) }));
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const endereco = await buscarEnderecoPorCep(cep);
      if (endereco) {
        setMotorista(atual => ({
          ...atual,
          logradouro: atual.logradouro || endereco.logradouro,
          bairro: atual.bairro || endereco.bairro,
          cidade: atual.cidade || endereco.cidade,
          estado: atual.estado || endereco.estado
        }));
      }
    } catch (e) {
      // CEP é só uma ajuda de preenchimento; sem ela o usuário digita normalmente.
    } finally {
      setBuscandoCep(false);
    }
  };

  const alterarVeiculo = (indice, alteracoes) => setVeiculos(atual => atual.map((veiculo, posicao) => (posicao === indice ? { ...veiculo, ...alteracoes } : veiculo)));

  const aplicarFrotista = (veiculo, frotista) => {
    const padrao = mascararTelefones(data?.proprietario_frotista || {}, CAMPOS_TELEFONE_VEICULO);
    const contatosPadrao = Object.fromEntries(CAMPOS_PROPRIETARIO.map(campo => [campo, padrao[campo] || '']));
    if (frotista) return { ...veiculo, ...contatosPadrao, frotista };
    // Ao virar terceiro, remove os contatos da Transben para não enviá-los como proprietário.
    const limpos = Object.fromEntries(CAMPOS_PROPRIETARIO.filter(campo => veiculo[campo] && veiculo[campo] === contatosPadrao[campo]).map(campo => [campo, '']));
    return { ...veiculo, ...limpos, frotista };
  };

  // As carretas seguem o cavalo; cada carreta ainda pode ser alterada individualmente.
  const alterarFrotista = (indice, frotista) => setVeiculos(atual => atual.map((veiculo, posicao) => (
    posicao === indice || (indice === 0 && posicao > 0) ? aplicarFrotista(veiculo, frotista) : veiculo
  )));

  const copiarProprietarioDoCavalo = indice => {
    const cavalo = veiculos[0] || {};
    alterarVeiculo(indice, { ...Object.fromEntries(CAMPOS_PROPRIETARIO.map(campo => [campo, cavalo[campo] || ''])), frotista: cavalo.frotista });
  };

  const selecionarArquivo = codigo => event => {
    const arquivo = event.target.files?.[0] || null;
    setErroArquivo('');
    if (arquivo && !['application/pdf', 'image/jpeg', 'image/png'].includes(arquivo.type)) {
      setErroArquivo('Envie o comprovante em PDF, JPG ou PNG.');
      return;
    }
    if (arquivo && arquivo.size > TAMANHO_MAXIMO_ARQUIVO) {
      setErroArquivo('O arquivo deve ter no máximo 10 MB.');
      return;
    }
    setArquivos(atual => ({ ...atual, [codigo]: arquivo }));
  };

  const avancar = () => setEtapa(atual => Math.min(atual + 1, REVISAR));
  const voltar = () => setEtapa(atual => Math.max(atual - 1, 0));

  const enviar = event => {
    event.preventDefault();
    if (etapa !== REVISAR) {
      if (pendenciasPorEtapa[etapa] === 0) avancar();
      return;
    }
    if (totalPendencias > 0 || loading) return;
    onSubmit({
      tipo_mercadoria: carga.tipo_mercadoria,
      valor: carga.valor,
      valor_carga: carga.valor_carga,
      motorista: { ...motorista, cep: somenteDigitos(motorista.cep) },
      cavalo: veiculos[0],
      carretas: veiculos.slice(1),
      arquivos: Object.fromEntries(Object.entries(arquivos).filter(([, arquivo]) => Boolean(arquivo)))
    });
  };

  const campoMotorista = (campo, rotulo, { md = 6, onBlur, maxLength, placeholder, telefone = false } = {}) => {
    const obrigatorio = MOTORISTA_OBRIGATORIOS.includes(campo);
    return (
      <Col md={md}>
        <Form.Group className="mb-2" controlId={`vx-motorista-${campo}`}>
          <Rotulo>{rotulo}{obrigatorio ? ' *' : ''}</Rotulo>
          <Form.Control
            size="sm"
            type={telefone ? 'tel' : 'text'}
            inputMode={telefone ? 'numeric' : undefined}
            value={motorista[campo] || ''}
            onChange={alterarMotorista(campo, telefone ? mascararTelefone : null)}
            onBlur={onBlur}
            maxLength={telefone ? 15 : maxLength}
            placeholder={telefone ? '(00) 00000-0000' : placeholder}
            disabled={loading}
            isInvalid={obrigatorio && vazio(motorista[campo])}
          />
        </Form.Group>
      </Col>
    );
  };

  const campoVeiculo = (indice, campo, rotulo, { telefone = false } = {}) => (
    <Col md={6}>
      <Form.Group className="mb-2" controlId={`vx-veiculo-${indice}-${campo}`}>
        <Rotulo>{rotulo}{VEICULO_OBRIGATORIOS.includes(campo) ? ' *' : ''}</Rotulo>
        <Form.Control
          size="sm"
          value={veiculos[indice]?.[campo] || ''}
          type={telefone ? 'tel' : 'text'}
          inputMode={telefone ? 'numeric' : undefined}
          maxLength={telefone ? 15 : undefined}
          onChange={event => alterarVeiculo(indice, { [campo]: telefone ? mascararTelefone(event.target.value) : event.target.value })}
          placeholder={telefone ? '(00) 00000-0000' : undefined}
          disabled={loading}
          isInvalid={VEICULO_OBRIGATORIOS.includes(campo) && vazio(veiculos[indice]?.[campo])}
        />
      </Form.Group>
    </Col>
  );

  const renderComprovante = () => (
    <>
      {documentos.map(documento => (
        <Cartao key={documento.codigo}>
          {documento.anexado && !trocar[documento.codigo] ? (
            <ArquivoAtual>
              <div>
                <strong>{documento.titulo}</strong>
                <div className="text-muted small">
                  Reaproveitando {documento.nome_arquivo}
                  {documento.data_anexo ? `, enviado em ${formatarData(documento.data_anexo)}` : ''}
                </div>
              </div>
              <LinkBotao type="button" onClick={() => setTrocar(atual => ({ ...atual, [documento.codigo]: true }))} disabled={loading}>Trocar arquivo</LinkBotao>
            </ArquivoAtual>
          ) : (
            <Form.Group controlId={`vx-documento-${documento.codigo}`}>
              <Form.Label>{documento.titulo} *</Form.Label>
              <Form.Control type="file" accept="application/pdf,image/jpeg,image/png" onChange={selecionarArquivo(documento.codigo)} disabled={loading} isInvalid={!arquivos[documento.codigo]} />
              <Form.Text muted>
                {arquivos[documento.codigo] ? `Selecionado: ${arquivos[documento.codigo].name}` : 'PDF, JPG ou PNG até 10 MB. Conta de luz, água ou telefone em nome do motorista.'}
              </Form.Text>
            </Form.Group>
          )}
        </Cartao>
      ))}
      {erroArquivo ? <div className="text-danger small mt-2">{erroArquivo}</div> : null}
    </>
  );

  const renderMotorista = () => (
    <>
      <GrupoTitulo>Endereço</GrupoTitulo>
      <Row>
        {campoMotorista('cep', buscandoCep ? 'CEP (buscando...)' : 'CEP', { md: 3, onBlur: completarPorCep, maxLength: 9, placeholder: '00000-000' })}
        {campoMotorista('logradouro', 'Logradouro', { md: 6 })}
        {campoMotorista('numero', 'Número', { md: 3 })}
        {campoMotorista('complemento', 'Complemento', { md: 3 })}
        {campoMotorista('bairro', 'Bairro', { md: 4 })}
        {campoMotorista('cidade', 'Cidade', { md: 3 })}
        {campoMotorista('estado', 'UF', { md: 2, maxLength: 2 })}
      </Row>
      <GrupoTitulo>Contato principal</GrupoTitulo>
      <Row>
        {campoMotorista('telefone_residencial', 'Nome do contato')}
        {campoMotorista('telefone_residencial_contato', 'Telefone do contato', { telefone: true })}
      </Row>
      <LinkBotao type="button" onClick={() => setContatosExtras(atual => !atual)}>
        {contatosExtras ? 'Ocultar contatos adicionais' : '+ Contato comercial ou de referência (opcional)'}
      </LinkBotao>
      {contatosExtras ? (
        <Row className="mt-2">
          {campoMotorista('telefone_comercial', 'Contato comercial', { md: 3 })}
          {campoMotorista('telefone_comercial_contato', 'Telefone comercial', { md: 3, telefone: true })}
          {campoMotorista('telefone_referencia', 'Contato de referência', { md: 3 })}
          {campoMotorista('telefone_referencia_contato', 'Telefone de referência', { md: 3, telefone: true })}
        </Row>
      ) : null}
    </>
  );

  const renderVeiculos = () => veiculos.map((veiculo, indice) => (
    <Cartao key={veiculo.placa || indice}>
      <CartaoTopo>
        <span>{nomeVeiculo(indice)} <Placa>{veiculo.placa}</Placa></span>
        <Status $ok={pendentesVeiculos[indice]?.length === 0}>{pendentesVeiculos[indice]?.length === 0 ? 'Pronto' : 'Pendente'}</Status>
      </CartaoTopo>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <Form.Check
          type="switch"
          id={`vx-frotista-${indice}`}
          label={indice === 0 ? 'Frota própria (aplica às carretas)' : 'Frota própria'}
          checked={Boolean(veiculo.frotista)}
          onChange={event => alterarFrotista(indice, event.target.checked)}
          disabled={loading}
        />
        {indice > 0 ? <LinkBotao type="button" onClick={() => copiarProprietarioDoCavalo(indice)} disabled={loading}>Mesmo proprietário do cavalo</LinkBotao> : null}
      </div>
      <Row>
        {campoVeiculo(indice, 'telefone_residencial_proprietario', 'Nome do contato do proprietário')}
        {campoVeiculo(indice, 'telefone_residencial_contato_proprietario', 'Telefone do proprietário', { telefone: true })}
      </Row>
      {comercialVeiculo[indice] ? (
        <Row>
          {campoVeiculo(indice, 'telefone_comercial_proprietario', 'Contato comercial (opcional)')}
          {campoVeiculo(indice, 'telefone_comercial_contato_proprietario', 'Telefone comercial (opcional)', { telefone: true })}
        </Row>
      ) : (
        <LinkBotao type="button" onClick={() => setComercialVeiculo(atual => ({ ...atual, [indice]: true }))} disabled={loading}>
          + Contato comercial do proprietário (opcional)
        </LinkBotao>
      )}
    </Cartao>
  ));

  const itemRevisao = (indiceEtapa, titulo, conteudo) => (
    <Cartao>
      <CartaoTopo>
        <span>{titulo}</span>
        <LinkBotao type="button" onClick={() => setEtapa(indiceEtapa)} disabled={loading}>Editar</LinkBotao>
      </CartaoTopo>
      <Resumo>{conteudo}</Resumo>
      {pendenciasPorEtapa[indiceEtapa] > 0 ? <div className="text-danger small mt-1">{pendenciasPorEtapa[indiceEtapa]} item(ns) pendente(s)</div> : null}
    </Cartao>
  );

  const renderCarga = () => (
    <VxCargaCampos
      opcoes={opcoesCarga}
      carregando={carregandoOpcoes}
      erro={erroOpcoes}
      valores={carga}
      onChange={setCarga}
      disabled={loading}
    />
  );

  const renderRevisao = () => {
    const resumoCarga = descreverCarga(carga, opcoesCarga);
    return (
      <>
        {itemRevisao(0, 'Carga', (
          <>
            <div>Mercadoria: {resumoCarga.mercadoria}</div>
            <div>Faixa: {resumoCarga.faixa}{resumoCarga.valorInformado ? ` (${resumoCarga.valorInformado} informado)` : ''}</div>
          </>
        ))}
        {itemRevisao(1, 'Comprovante de endereço', documentos.map(documento => (
          <div key={documento.codigo}>
            {arquivos[documento.codigo]?.name || (documento.anexado && !trocar[documento.codigo] ? documento.nome_arquivo : 'Não anexado')}
          </div>
        )))}
        {itemRevisao(2, 'Motorista', (
          <>
            <div>{resumoEndereco(motorista) || 'Endereço não informado'}</div>
            <div>Contato: {motorista.telefone_residencial || '-'} {motorista.telefone_residencial_contato || ''}</div>
          </>
        ))}
        {itemRevisao(3, 'Veículos', veiculos.map((veiculo, indice) => (
          <div key={veiculo.placa || indice}>
            {nomeVeiculo(indice)} {veiculo.placa} · {veiculo.frotista ? 'Frota própria' : 'Terceiro'} · {veiculo.telefone_residencial_proprietario || '-'} {veiculo.telefone_residencial_contato_proprietario || ''}
          </div>
        )))}
      </>
    );
  };

  const conteudoEtapa = [renderCarga, renderComprovante, renderMotorista, renderVeiculos, renderRevisao][etapa];
  const aviso = erro || data?.mensagem;
  const ultimaEtapa = etapa === REVISAR;
  const podeAvancar = pendenciasPorEtapa[etapa] === 0;

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop={loading ? 'static' : true} centered scrollable>
      <FormLayout onSubmit={enviar}>
        <Modal.Header closeButton={!loading}>
          <HeaderContent>
            <div>
              <Modal.Title>Nova consulta VX</Modal.Title>
              <Subtitulo>
                {data?.nome_motorista || row?.nome || 'Motorista'} · CPF {data?.cpf || row?.cpf || '-'} · {veiculos.map(veiculo => veiculo.placa).join(' / ')}
              </Subtitulo>
            </div>
            <VxConsultLogo size="sm" />
          </HeaderContent>
        </Modal.Header>

        {data ? (
          <Stepper aria-label="Etapas da consulta VX">
            {ETAPAS.map((item, indice) => {
              const concluida = indice !== REVISAR && pendenciasPorEtapa[indice] === 0;
              return (
                <Passo
                  key={item.id}
                  type="button"
                  onClick={() => setEtapa(indice)}
                  disabled={loading}
                  aria-current={indice === etapa ? 'step' : undefined}
                  $ativo={indice === etapa}
                >
                  <Bolinha $ativo={indice === etapa} $concluida={concluida} $pendente={pendenciasPorEtapa[indice] > 0}>
                    {concluida && indice !== etapa ? '✓' : indice + 1}
                  </Bolinha>
                  <PassoTexto>
                    <strong>{item.titulo}</strong>
                    <small>
                      {indice === REVISAR ? 'Enviar' : pendenciasPorEtapa[indice] > 0 ? `${pendenciasPorEtapa[indice]} pendente(s)` : 'Pronto'}
                    </small>
                  </PassoTexto>
                </Passo>
              );
            })}
          </Stepper>
        ) : null}

        <Corpo>
          {!data ? <p className="text-muted mb-0">Carregando dados da consulta...</p> : (
            <>
              <EtapaDescricao>{ETAPAS[etapa].descricao} Os dados vêm do TMS e das consultas anteriores.</EtapaDescricao>
              {aviso ? <div className="alert alert-warning py-2">{aviso}</div> : null}
              {conteudoEtapa()}
            </>
          )}
        </Corpo>

        <Modal.Footer className="justify-content-between">
          <Progresso $ok={totalPendencias === 0}>
            {totalPendencias === 0 ? 'Tudo pronto para enviar à VX.' : `Faltam ${totalPendencias} item(ns) para criar a consulta.`}
          </Progresso>
          <div className="d-flex gap-2">
            {etapa > 0 ? (
              <ButtonWithLoading variant="outline-secondary" type="button" onClick={voltar} disabled={loading}>Voltar</ButtonWithLoading>
            ) : (
              <ButtonWithLoading variant="outline-secondary" type="button" onClick={onClose} disabled={loading}>Cancelar</ButtonWithLoading>
            )}
            {ultimaEtapa ? (
              <ButtonWithLoading variant="primary" type="submit" disabled={!data || totalPendencias > 0} loading={loading} loadingText="Criando consulta...">
                Criar consulta VX
              </ButtonWithLoading>
            ) : (
              <ButtonWithLoading variant="primary" type="submit" disabled={!data || !podeAvancar || loading} title={podeAvancar ? undefined : 'Complete os campos marcados para avançar'}>
                Próximo: {ETAPAS[etapa + 1].titulo}
              </ButtonWithLoading>
            )}
          </div>
        </Modal.Footer>
      </FormLayout>
    </Modal>
  );
};

ProgramacaoHavanVxPesquisaModal.propTypes = {
  show: PropTypes.bool,
  row: PropTypes.shape(),
  data: PropTypes.shape(),
  opcoesCarga: PropTypes.shape(),
  carregandoOpcoes: PropTypes.bool,
  erroOpcoes: PropTypes.string,
  loading: PropTypes.bool,
  erro: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

ProgramacaoHavanVxPesquisaModal.defaultProps = {
  show: false,
  row: null,
  data: null,
  opcoesCarga: null,
  carregandoOpcoes: false,
  erroOpcoes: '',
  loading: false,
  erro: ''
};

export default ProgramacaoHavanVxPesquisaModal;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  padding-right: 16px;
`;

const Subtitulo = styled.div`
  margin-top: 2px;
  color: #6c757d;
  font-size: 13px;
`;

const Stepper = styled.nav`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  padding: 8px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid #e3e8ef;
  background: #f8f9fb;
`;

const Passo = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid ${({ $ativo }) => ($ativo ? '#0d6efd' : 'transparent')};
  border-radius: 8px;
  background: ${({ $ativo }) => ($ativo ? '#ffffff' : 'transparent')};
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #ffffff;
  }
`;

const Bolinha = styled.span`
  display: inline-flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${({ $ativo, $concluida, $pendente }) => {
    if ($ativo) return '#0d6efd';
    if ($concluida) return '#1e7b34';
    if ($pendente) return '#fff4e5';
    return '#e9ecef';
  }};
  color: ${({ $ativo, $concluida, $pendente }) => {
    if ($ativo || $concluida) return '#ffffff';
    if ($pendente) return '#a15c00';
    return '#495057';
  }};
  font-size: 13px;
  font-weight: 700;
`;

const PassoTexto = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: #24344d;
  font-size: 13px;
  line-height: 1.2;

  small {
    color: #6c757d;
    font-size: 11px;
    white-space: nowrap;
  }
`;

// O Form envolve cabeçalho, corpo e rodapé; precisa ser coluna flex para o modal "scrollable"
// rolar só o corpo e manter o rodapé (Voltar/Próximo) sempre visível.
const FormLayout = styled(Form)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
`;

const Corpo = styled(Modal.Body)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 20px;
`;

const Rotulo = styled(Form.Label)`
  margin-bottom: 2px;
  color: #495057;
  font-size: 12px;
  font-weight: 600;
`;

const EtapaDescricao = styled.p`
  margin-bottom: 12px;
  color: #6c757d;
  font-size: 13px;
`;

const GrupoTitulo = styled.div`
  margin: 8px 0 6px;
  color: #6c757d;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Cartao = styled.section`
  margin-bottom: 10px;
  padding: 10px 14px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
`;

const CartaoTopo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #24344d;
  font-size: 15px;
  font-weight: 600;
`;

const Status = styled.span`
  padding: 2px 10px;
  border-radius: 999px;
  background: ${({ $ok }) => ($ok ? '#e6f4ea' : '#fff4e5')};
  color: ${({ $ok }) => ($ok ? '#1e7b34' : '#a15c00')};
  font-size: 12px;
  font-weight: 600;
`;

const Resumo = styled.div`
  color: #495057;
  font-size: 13px;
  line-height: 1.6;
`;

const ArquivoAtual = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const LinkBotao = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: #0d6efd;
  font-size: 13px;
  text-decoration: underline;

  &:disabled {
    color: #6c757d;
  }
`;

const Placa = styled.span`
  margin-left: 6px;
  padding: 1px 8px;
  border: 1px solid #adb5bd;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
`;

const Progresso = styled.span`
  color: ${({ $ok }) => ($ok ? '#1e7b34' : '#a15c00')};
  font-size: 13px;
  font-weight: 600;
`;
